import { Controller } from 'stimulus'
import * as d3 from 'd3'
import {
  createChartFrame,
  drawHorizontalLine,
  drawXTickLabel,
  drawYTickLabel,
  formatTickValue,
  SERIES_COLORS,
  type ChartFrame,
  type SeriesColor,
} from '../charts/vitals-chart-scaffold'

interface LineChartSeries {
  name: string
  color: SeriesColor
  values: number[]
}

interface LineChartPayload {
  point_labels: string[]
  series: LineChartSeries[]
  y_min: number | null
  y_max: number | null
  y_tick_count: number
  y_tick_suffix: string
  y_label_step: number
  reference_value: number | null
  gridlines: 'none' | 'solid' | 'dashed'
  x_label_mode: 'ends' | 'all'
}

const GRIDLINE_OPACITY = 0.4
const POINT_RADIUS = 3.5

export default class extends Controller {
  static values = { payload: Object }

  declare payloadValue: LineChartPayload

  private boundRender = this.render.bind(this)

  connect(): void {
    this.render()
    window.addEventListener('resize', this.boundRender)
  }

  disconnect(): void {
    window.removeEventListener('resize', this.boundRender)
  }

  private render(): void {
    const payload = this.payloadValue
    if (!payload.point_labels?.length || !payload.series?.length) return

    this.element.innerHTML = ''
    const frame = createChartFrame(this.element)

    const xScale = d3
      .scalePoint<string>()
      .domain(payload.point_labels.map((_, index) => index.toString()))
      .range([0, frame.innerWidth])
      .padding(0.5)
    const pointX = (index: number): number => xScale(index.toString()) ?? 0

    const yScale = this.buildYScale(payload, frame.innerHeight)

    this.drawYAxis(payload, frame, yScale)
    this.drawXLabels(payload, frame, pointX)
    drawHorizontalLine(frame, frame.innerHeight)

    if (payload.reference_value !== null) {
      drawHorizontalLine(frame, yScale(payload.reference_value), {
        dashed: true,
      })
    }

    for (const series of payload.series) {
      this.drawSeries(series, payload.point_labels, frame, pointX, yScale)
    }
  }

  private buildYScale(
    payload: LineChartPayload,
    innerHeight: number,
  ): d3.ScaleLinear<number, number> {
    if (payload.y_min !== null && payload.y_max !== null) {
      return d3
        .scaleLinear()
        .domain([payload.y_min, payload.y_max])
        .range([innerHeight, 0])
    }
    const values = payload.series.flatMap((series) => series.values)
    if (payload.reference_value !== null) values.push(payload.reference_value)
    const [min, max] = d3.extent(values) as [number, number]
    const pad = min === max ? 1 : (max - min) / (payload.y_tick_count - 1) / 2
    return d3
      .scaleLinear()
      .domain([min - pad, max + pad])
      .nice(payload.y_tick_count)
      .range([innerHeight, 0])
  }

  private drawYAxis(
    payload: LineChartPayload,
    frame: ChartFrame,
    yScale: d3.ScaleLinear<number, number>,
  ): void {
    const ticks = this.yTicks(payload, yScale)
    ticks.forEach((tick, index) => {
      if (payload.gridlines !== 'none') {
        drawHorizontalLine(frame, yScale(tick), {
          dashed: payload.gridlines === 'dashed',
          opacity: GRIDLINE_OPACITY,
        })
      }
      if (index % payload.y_label_step === 0) {
        drawYTickLabel(
          frame,
          yScale(tick),
          `${formatTickValue(tick)}${payload.y_tick_suffix}`,
        )
      }
    })
  }

  private yTicks(
    payload: LineChartPayload,
    yScale: d3.ScaleLinear<number, number>,
  ): number[] {
    const { y_min: yMin, y_max: yMax, y_tick_count: tickCount } = payload
    if (yMin !== null && yMax !== null) {
      const step = (yMax - yMin) / (tickCount - 1)
      return d3.range(tickCount).map((index) => yMin + step * index)
    }
    return yScale.ticks(tickCount)
  }

  private drawXLabels(
    payload: LineChartPayload,
    frame: ChartFrame,
    pointX: (index: number) => number,
  ): void {
    const lastIndex = payload.point_labels.length - 1
    if (payload.x_label_mode === 'ends' && lastIndex > 0) {
      drawXTickLabel(frame, pointX(0), 'start', payload.point_labels[0])
      drawXTickLabel(
        frame,
        pointX(lastIndex),
        'end',
        payload.point_labels[lastIndex],
      )
      return
    }
    payload.point_labels.forEach((label, index) => {
      drawXTickLabel(frame, pointX(index), 'middle', label)
    })
  }

  private drawSeries(
    series: LineChartSeries,
    pointLabels: string[],
    frame: ChartFrame,
    pointX: (index: number) => number,
    yScale: d3.ScaleLinear<number, number>,
  ): void {
    if (series.values.length > 1) {
      const line = d3
        .line<number>()
        .x((_, index) => pointX(index))
        .y((value) => yScale(value))
      frame.chart
        .append('path')
        .attr('d', line(series.values))
        .attr('fill', 'none')
        .attr('stroke-width', 2)
        .style('stroke', SERIES_COLORS[series.color])
    }

    series.values.forEach((value, index) => {
      frame.chart
        .append('circle')
        .attr('cx', pointX(index))
        .attr('cy', yScale(value))
        .attr('r', POINT_RADIUS)
        .style('fill', SERIES_COLORS[series.color])
        .append('title')
        .text(
          `${series.name} — ${pointLabels[index]}: ${formatTickValue(value)}`,
        )
    })
  }
}
